<template>
  <MarketplaceCard
    v-if="settings && config"
    v-model="config.gpi_enabled"
    logo-alt="Global Payments"
    description="Industry-leading payment solutions. Global Payments Integrated is a true partnership in every sense of the word."
    learn-more-link="https://www.globalpayments.com/industries/services"
    cy-selector="marketplaceGPI"
  >
    <template #logo>
      <GlobalPaymentsLogo class="w-auto max-w-[180px] mr-auto my-2" />
    </template>
    <template #configure>
      <div class="link" @click="openModal">Set Up Information</div>
    </template>
    <template #modal>
      <div>
        <Modal
          v-if="modalOpen"
          title="Set Up Information - Global Payments"
          class-name="gpi-config-modal"
          :disabled="!isValid"
          @submit="handleSave"
          @hide-modal="closeModal"
        >
          <fieldset>
            <legend>Credit Card</legend>
            <InputField
              v-model="config.gpi_x_web_id"
              class="!mb-0"
              title="Web ID"
            />
            <InputField
              v-model="config.gpi_terminal_id"
              class="!mb-0"
              title="Terminal ID"
            />
            <InputField
              v-model="config.gpi_auth_key"
              class="!mb-0"
              title="Auth Key"
              type="password"
              toggleable
            />
          </fieldset>
          <fieldset>
            <legend>ACH</legend>
            <InputField
              v-model="config.gpi_ach_x_web_id"
              class="!mb-0"
              title="Web ID"
            />
            <InputField
              v-model="config.gpi_ach_terminal_id"
              class="!mb-0"
              title="Terminal ID"
            />
            <InputField
              v-model="config.gpi_ach_auth_key"
              class="!mb-0"
              title="Auth Key"
              type="password"
              toggleable
            />
          </fieldset>
          <hr class="border-gray-200 mt-2 mb-1" />
          <div id="shared-fields" class="d-flex flex-column">
            <Checkbox
              v-model="config.gpi_card_savable"
              label="Allow customers to save their payment method"
              class="mt-2"
            />
          </div>
        </Modal>
      </div>
    </template>
  </MarketplaceCard>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { useStore } from "vuex";

import Modal from "@/components/Modal.vue";
import Checkbox from "@/components/Seranova/Checkbox/Checkbox.vue";
import InputField from "@/components/Seranova/InputField/InputField.vue";
import GlobalPaymentsLogo from "@/images/global_payments_logo.svg?component";
import MarketplaceCard from "./MarketplaceCard.vue";

const store = useStore();

const settings = computed(
  () => store.state.paymentMethodsSettings?.payment_methods ?? null
);

const modalOpen = ref(false);

interface GpiConfig {
  gpi_x_web_id: string;
  gpi_terminal_id: string;
  gpi_auth_key: string;
  gpi_ach_x_web_id: string;
  gpi_ach_terminal_id: string;
  gpi_ach_auth_key: string;
  gpi_enabled: boolean;
  gpi_card_savable: boolean;
}

const config = ref<GpiConfig | null>(null);

function initForm() {
  if (settings.value) {
    config.value = {
      gpi_x_web_id: settings.value.gpi_x_web_id,
      gpi_terminal_id: settings.value.gpi_terminal_id,
      gpi_auth_key: settings.value.gpi_auth_key,
      gpi_ach_x_web_id: settings.value.gpi_ach_x_web_id,
      gpi_ach_terminal_id: settings.value.gpi_ach_terminal_id,
      gpi_ach_auth_key: settings.value.gpi_ach_auth_key,
      gpi_enabled: settings.value.gpi_enabled,
      gpi_card_savable: settings.value.gpi_card_savable
    };
  }
}

function fieldsValid(fields: (string | undefined | null)[]) {
  if (fields.some((f) => Boolean(f))) {
    return fields.every((f) => Boolean(f));
  }
  return true;
}

const isValid = computed(() => {
  if (!config.value) return false;

  const cardFields = [
    config.value.gpi_x_web_id,
    config.value.gpi_terminal_id,
    config.value.gpi_auth_key
  ];
  const achFields = [
    config.value.gpi_ach_x_web_id,
    config.value.gpi_ach_terminal_id,
    config.value.gpi_ach_auth_key
  ];

  return fieldsValid(cardFields) && fieldsValid(achFields);
});

function openModal() {
  modalOpen.value = true;
}

function closeModal() {
  modalOpen.value = false;
}

function handleSave() {
  if (!isValid.value || !config.value) return;

  const hasCc =
    Boolean(config.value.gpi_x_web_id) &&
    Boolean(config.value.gpi_terminal_id) &&
    Boolean(config.value.gpi_auth_key);

  const params: Record<string, unknown> = {
    gpi_x_web_id: config.value.gpi_x_web_id,
    gpi_terminal_id: config.value.gpi_terminal_id,
    gpi_auth_key: config.value.gpi_auth_key,
    gpi_ach_x_web_id: config.value.gpi_ach_x_web_id,
    gpi_ach_terminal_id: config.value.gpi_ach_terminal_id,
    gpi_ach_auth_key: config.value.gpi_ach_auth_key,
    gpi_card_savable: config.value.gpi_card_savable
  };

  let successMsg = "Global Payments settings saved!";

  if (!hasCc) {
    params.gpi_enabled = false;
    successMsg +=
      "<br /><br />GPI has been automatically disabled due to blank card settings.";
  }

  store
    .dispatch("updatePaymentMethodsSettings", { payment_methods: params })
    .then(() => {
      window.notify.success(successMsg);
      closeModal();
    });
}

function handleCheckChange(field: string, value: boolean) {
  store
    .dispatch("updatePaymentMethodsSettings", {
      payment_methods: { [field]: value }
    })
    .then(() => {
      window.notify.success("Global Payments settings saved!");
    })
    .catch(() => {
      initForm();
    });
}

watch(settings, (newValue, oldValue) => {
  if (newValue !== oldValue && !config.value) {
    initForm();
  }
});

watch(
  () => config.value?.gpi_enabled,
  (newValue, oldValue) => {
    if (newValue !== oldValue && oldValue !== undefined) {
      if (newValue !== settings.value?.gpi_enabled) {
        handleCheckChange("gpi_enabled", newValue as boolean);
      }
    }
  }
);

watch(modalOpen, (newValue, oldValue) => {
  if (newValue !== oldValue && !newValue) {
    initForm();
  }
});

onMounted(() => {
  initForm();
});
</script>

<style scoped lang="scss">
fieldset {
  border-radius: 6px;
  border: 1px solid var(--sera-color-fields-border);
  padding: 10px;
  margin-bottom: 12px;
}

legend {
  width: auto;
  font-size: 15px;
  font-weight: bold;
  margin: 0;
  padding: 0 5px;
}
</style>
